import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import StockPriceChart from '../Components/StockPriceChart';
import StockStatsCard from '../Components/StockStatsCard';
import './Stocks.css';

/** 'CA' | 'US' | null (unknown — only shown when filter is All). */
function getStockRegion(stock) {
    const c = (stock.country || '').trim().toLowerCase();
    if (c.includes('canada')) return 'CA';
    if (
        c.includes('united states') ||
        c === 'usa' ||
        c === 'us' ||
        /\bu\.s\.a?\.?\b/.test(c)
    ) {
        return 'US';
    }

    const sym = (stock.symbol || '').toUpperCase();
    if (sym.endsWith('.TO') || sym.endsWith('.TSX') || sym.endsWith('.V') || sym.endsWith('.CN')) {
        return 'CA';
    }
    if (sym.endsWith('.US')) return 'US';

    const ex = (stock.exchange || '').toUpperCase();
    if (
        ex.includes('TOR') ||
        ex.includes('TSX') ||
        ex.includes('TSXV') ||
        ex.includes('CNQ') ||
        ex === 'VAN' ||
        ex === 'NEO' ||
        ex === 'CSE'
    ) {
        return 'CA';
    }
    if (
        ex.includes('NMS') ||
        ex.includes('NASDAQ') ||
        ex.includes('NYSE') ||
        ex.includes('NYQ') ||
        ex === 'ASE' ||
        ex === 'PCX' ||
        ex === 'BATS' ||
        ex === 'PNK' ||
        ex === 'OTC'
    ) {
        return 'US';
    }
    return null;
}

const REGION_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'CA', label: 'Canada' },
    { id: 'US', label: 'United States' },
];

/** Calendar date in local timezone, YYYY-MM-DD */
function formatLocalYMD(d) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${da}`;
}

function parseYMD(s) {
    const [y, m, d] = String(s).split('-').map(Number);
    return new Date(y, m - 1, d);
}

function subtractOneYear(ymd) {
    const d = parseYMD(ymd);
    d.setFullYear(d.getFullYear() - 1);
    return formatLocalYMD(d);
}

function dayBefore(ymd) {
    const d = parseYMD(ymd);
    d.setDate(d.getDate() - 1);
    return formatLocalYMD(d);
}

/**
 * How much stored history the detail view shows. This is the only control over
 * what the chart and tables display: picking one reloads that span from the
 * database. It is deliberately independent of the download range below, which
 * only decides what gets pulled from Yahoo.
 */
const DISPLAY_RANGES = [
    { id: '1M', label: '1 month' },
    { id: '6M', label: '6 months' },
    { id: 'YTD', label: 'YTD' },
    { id: '1Y', label: '1 year' },
    { id: '5Y', label: '5 years' },
    { id: 'MAX', label: 'Everything on file' },
];

const DEFAULT_DISPLAY_RANGE = '1Y';

/** Lower bound for a display range, anchored on the newest day on file. '' means no bound. */
function displayRangeStart(rangeId, anchorYMD) {
    if (rangeId === 'MAX' || !anchorYMD) return '';
    const d = parseYMD(anchorYMD);
    switch (rangeId) {
        case '1M':
            d.setMonth(d.getMonth() - 1);
            break;
        case '6M':
            d.setMonth(d.getMonth() - 6);
            break;
        case 'YTD':
            return `${d.getFullYear()}-01-01`;
        case '1Y':
            d.setFullYear(d.getFullYear() - 1);
            break;
        case '5Y':
            d.setFullYear(d.getFullYear() - 5);
            break;
        default:
            return '';
    }
    return formatLocalYMD(d);
}

const DISPLAY_RANGE_LABELS = Object.fromEntries(DISPLAY_RANGES.map(r => [r.id, r.label]));

/** Download window presets — all end today, so a download always reaches the latest session. */
const DOWNLOAD_PRESETS = [
    { id: 'CATCHUP', label: 'Since last on file' },
    { id: '1M', label: '1 month' },
    { id: '6M', label: '6 months' },
    { id: '1Y', label: '1 year' },
    { id: '5Y', label: '5 years' },
    { id: '10Y', label: '10 years' },
    { id: '20Y', label: '20 years' },
];

/** Refresh cadence for live quotes — matches the quotes service cache while the market is open. */
const LIVE_POLL_MS = 60 * 1000;
/** While the market is closed there is nothing to tick; this only catches the open. */
const CLOSED_POLL_MS = 15 * 60 * 1000;

/**
 * Calls `tick` every `ms` for as long as the tab is visible, and once more as
 * soon as it becomes visible again. A backgrounded tab is never polled.
 * Returns the cleanup function a useEffect should hand back.
 */
function pollWhileVisible(tick, ms, { immediate = false } = {}) {
    let timer = null;
    const stop = () => {
        if (timer !== null) {
            clearInterval(timer);
            timer = null;
        }
    };
    const start = () => {
        if (timer === null) timer = setInterval(tick, ms);
    };
    const onVisibilityChange = () => {
        if (document.hidden) {
            stop();
        } else {
            tick();
            start();
        }
    };

    if (!document.hidden) {
        if (immediate) tick();
        start();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
        stop();
        document.removeEventListener('visibilitychange', onVisibilityChange);
    };
}

/** Up/down colour class for a signed move; nothing for flat or unknown. */
function moveClass(v) {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '';
    return n > 0 ? 'stock-period-up' : 'stock-period-down';
}

function formatClockLocal(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const Stocks = () => {
    const [stocks, setStocks] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [error, setError] = useState('');
    const [symbol, setSymbol] = useState('');
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState(null);

    const [detailStock, setDetailStock] = useState(null);
    const [detailViewMode, setDetailViewMode] = useState('prices');
    const [quotePayload, setQuotePayload] = useState(null);
    const [dividendPayload, setDividendPayload] = useState(null);
    const [splitPayload, setSplitPayload] = useState(null);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [fetchingQuotes, setFetchingQuotes] = useState(false);
    const [downloadNote, setDownloadNote] = useState('');
    /** Display side: which span of stored history is loaded, and its lower bound. */
    const [displayRange, setDisplayRange] = useState(DEFAULT_DISPLAY_RANGE);
    const [displayStart, setDisplayStart] = useState('');
    /** Download side: the Yahoo window. Never affects what is displayed. */
    const [downloadStart, setDownloadStart] = useState('');
    const [downloadEnd, setDownloadEnd] = useState('');
    const [showPriceRows, setShowPriceRows] = useState(false);
    const [regionFilter, setRegionFilter] = useState('all');
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [statsError, setStatsError] = useState('');
    /** Live batch quotes for the watchlist cards, keyed by symbol. */
    const [liveQuotes, setLiveQuotes] = useState({});
    const [liveMarketOpen, setLiveMarketOpen] = useState(false);
    /** The symbol whose detail view is open — a reply for any other is dropped. */
    const detailSymbolRef = useRef(null);
    /** Price table only — oldest→newest; chart uses quotePayload. */
    const [tableQuotes, setTableQuotes] = useState([]);
    const [tableOldestLoaded, setTableOldestLoaded] = useState(null);
    const [tableHasMore, setTableHasMore] = useState(false);
    const [loadingMoreTable, setLoadingMoreTable] = useState(false);

    const fetchStocks = useCallback(async (opts = {}) => {
        const silent = Boolean(opts.silent);
        if (!silent) setLoadingList(true);
        setError('');
        try {
            const r = await fetch('/api/stocks', { credentials: 'include' });
            if (!r.ok) throw new Error('Failed to load stocks');
            setStocks(await r.json());
        } catch (err) {
            setError(err.message);
        } finally {
            if (!silent) setLoadingList(false);
        }
    }, []);

    useEffect(() => { fetchStocks(); }, [fetchStocks]);

    const sortedStocks = useMemo(
        () => [...stocks].sort((a, b) =>
            a.symbol.localeCompare(b.symbol, undefined, { sensitivity: 'base', numeric: true })),
        [stocks],
    );

    const displayedStocks = useMemo(() => {
        if (regionFilter === 'all') return sortedStocks;
        return sortedStocks.filter(s => getStockRegion(s) === regionFilter);
    }, [sortedStocks, regionFilter]);

    const quotes = useMemo(() => quotePayload?.quotes || [], [quotePayload]);
    const dividendRows = useMemo(() => dividendPayload?.dividends || [], [dividendPayload]);
    const splitRows = useMemo(() => splitPayload?.splits || [], [splitPayload]);

    /** Dividend rows shaped like quotes so the price chart can plot them. */
    const dividendRowsAsQuotes = useMemo(
        () => dividendRows.map(d => ({ ...d, closing: d.amount })),
        [dividendRows],
    );

    const tableRowsNewestFirst = useMemo(() => [...tableQuotes].reverse(), [tableQuotes]);
    const dividendRowsNewestFirst = useMemo(() => [...dividendRows].reverse(), [dividendRows]);
    const splitRowsNewestFirst = useMemo(() => [...splitRows].reverse(), [splitRows]);

    /** Summary of the loaded window — change, extremes and liquidity at a glance. */
    const periodSummary = useMemo(() => {
        if (!quotes.length) return null;
        const first = quotes[0];
        const last = quotes[quotes.length - 1];
        let high = -Infinity;
        let low = Infinity;
        let volumeSum = 0;
        for (const row of quotes) {
            if (row.high > high) high = row.high;
            if (row.low < low) low = row.low;
            volumeSum += row.volume || 0;
        }
        const change = last.closing - first.closing;
        return {
            firstDate: first.date,
            lastDate: last.date,
            firstClose: first.closing,
            lastClose: last.closing,
            change,
            changePercent: first.closing ? (change / first.closing) * 100 : null,
            high,
            low,
            avgVolume: volumeSum / quotes.length,
            sessions: quotes.length,
            dividendTotal: dividendRows.reduce((sum, d) => sum + (d.amount || 0), 0),
        };
    }, [quotes, dividendRows]);

    /**
     * The chart can only draw what the database holds, so say so plainly when the
     * requested span reaches further back than the stored history.
     */
    const coverageShortfall = useMemo(() => {
        const min = quotePayload?.stored_range?.min;
        if (!min || !displayStart) return null;
        return min > displayStart ? min : null;
    }, [quotePayload?.stored_range?.min, displayStart]);

    const resetTableQuotes = useCallback(() => {
        setTableQuotes([]);
        setTableOldestLoaded(null);
        setTableHasMore(false);
        setLoadingMoreTable(false);
    }, []);

    const initTableFromQuotes = useCallback((rows, floor) => {
        if (!rows.length) {
            setTableQuotes([]);
            setTableOldestLoaded(null);
            setTableHasMore(false);
            return;
        }
        const end = rows[rows.length - 1].date;
        const tableStart = subtractOneYear(end);
        const slice = rows.filter(q => q.date >= tableStart);
        setTableQuotes(slice);
        const oldest = slice.length ? slice[0].date : tableStart;
        setTableOldestLoaded(oldest);
        setTableHasMore(Boolean(floor && oldest > floor));
    }, []);

    const fetchQuotesForSymbol = useCallback(async (sym, start, end) => {
        const params = new URLSearchParams();
        if (start) params.set('start', start);
        if (end) params.set('end', end);
        const qs = params.toString();
        const r = await fetch(
            `/api/stocks/${encodeURIComponent(sym)}/quotes${qs ? `?${qs}` : ''}`,
            { credentials: 'include' },
        );
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || 'Failed to load quotes');
        return data;
    }, []);

    /**
     * Loads the stored prices, dividends and splits for one display range.
     * `anchor` is the newest day on file, used only to work out the lower bound.
     */
    const loadDetailData = useCallback(async (sym, rangeId, anchor) => {
        const start = displayRangeStart(rangeId, anchor);
        setLoadingQuotes(true);
        setError('');
        resetTableQuotes();
        setDisplayStart(start);
        try {
            const qs = start ? `?start=${encodeURIComponent(start)}` : '';
            const base = `/api/stocks/${encodeURIComponent(sym)}`;

            const [rq, rd, rs] = await Promise.all([
                fetch(`${base}/quotes${qs}`, { credentials: 'include' }),
                fetch(`${base}/dividends${qs}`, { credentials: 'include' }),
                fetch(`${base}/splits${qs}`, { credentials: 'include' }),
            ]);

            const quoteData = await rq.json();
            if (!rq.ok) throw new Error(quoteData.error || 'Failed to load quotes');
            setQuotePayload(quoteData);

            const loadedQuotes = quoteData.quotes || [];
            initTableFromQuotes(loadedQuotes, start || quoteData.stored_range?.min);

            const divData = await rd.json();
            setDividendPayload(
                rd.ok
                    ? divData
                    : {
                          symbol: sym,
                          dividends: [],
                          total_rows: 0,
                          returned_rows: 0,
                          truncated: false,
                          load_error: divData.error || 'Failed to load dividends',
                      },
            );

            const splitData = await rs.json();
            setSplitPayload(
                rs.ok
                    ? splitData
                    : {
                          symbol: sym,
                          splits: [],
                          total_rows: 0,
                          returned_rows: 0,
                          truncated: false,
                          load_error: splitData.error || 'Failed to load splits',
                      },
            );
        } catch (err) {
            setError(err.message);
            setQuotePayload(null);
            setDividendPayload(null);
            setSplitPayload(null);
            resetTableQuotes();
        } finally {
            setLoadingQuotes(false);
        }
    }, [resetTableQuotes, initTableFromQuotes]);

    /**
     * Loads the statistics panel. A background refresh (the live poll) never
     * shows a spinner and never clears the panel: a failed poll leaves the last
     * good quote on screen, since only an explicit load reports an error.
     */
    const loadStats = useCallback(async (sym, opts = {}) => {
        const background = Boolean(opts.background);
        if (!background) {
            setLoadingStats(true);
            setStatsError('');
        }
        try {
            const r = await fetch(`/api/stocks/${encodeURIComponent(sym)}/stats`, {
                credentials: 'include',
            });
            const data = await r.json();
            if (detailSymbolRef.current !== sym) return;
            if (!r.ok) throw new Error(data.error || 'Failed to load statistics');
            setStats(data);
            setStatsError('');
        } catch (err) {
            if (detailSymbolRef.current !== sym || background) return;
            setStats(null);
            setStatsError(err.message);
        } finally {
            // A reply for a symbol the user has already navigated away from must
            // not clear the spinner the newly opened symbol is waiting on.
            if (!background && detailSymbolRef.current === sym) setLoadingStats(false);
        }
    }, []);

    /**
     * Live quotes for every symbol on the list, in one call. Purely additive —
     * on failure the cards keep whatever was last fetched and fall back to the
     * stored close, so there is nothing to report to the user.
     */
    const loadLiveQuotes = useCallback(async () => {
        try {
            const r = await fetch('/api/stocks/snapshot', { credentials: 'include' });
            const data = await r.json();
            if (!r.ok) return;
            const rows = Array.isArray(data.quotes) ? data.quotes : [];
            setLiveQuotes(Object.fromEntries(rows.map(row => [row.symbol, row])));
            setLiveMarketOpen(Boolean(data.market_open));
        } catch {
            /* keep the last good quotes */
        }
    }, []);

    const loadMoreTable = useCallback(async () => {
        if (!detailStock || !tableOldestLoaded || loadingMoreTable || !tableHasMore) return;
        setLoadingMoreTable(true);
        setError('');
        try {
            const newEnd = dayBefore(tableOldestLoaded);
            let newStart = subtractOneYear(newEnd);
            const floor = displayStart || quotePayload?.stored_range?.min;
            if (floor && newStart < floor) {
                newStart = floor;
            }
            const data = await fetchQuotesForSymbol(detailStock.symbol, newStart, newEnd);
            const older = data.quotes || [];
            setTableQuotes(prev => [...older, ...prev]);
            const oldest = older.length ? older[0].date : newStart;
            setTableOldestLoaded(oldest);
            setTableHasMore(Boolean(floor && oldest > floor));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoadingMoreTable(false);
        }
    }, [
        detailStock,
        tableOldestLoaded,
        loadingMoreTable,
        tableHasMore,
        displayStart,
        quotePayload?.stored_range?.min,
        fetchQuotesForSymbol,
    ]);

    /** Newest day on file for this symbol, as currently known. */
    const storedAnchor = () =>
        quotePayload?.stored_range?.max || detailStock?.last_quote_date || formatLocalYMD(new Date());

    const openDetail = (stock) => {
        const today = formatLocalYMD(new Date());
        detailSymbolRef.current = stock.symbol;
        setDetailStock(stock);
        setQuotePayload(null);
        setDividendPayload(null);
        setSplitPayload(null);
        setStats(null);
        setStatsError('');
        setDownloadNote('');
        setDetailViewMode('prices');
        setShowPriceRows(false);
        setDisplayRange(DEFAULT_DISPLAY_RANGE);
        setDisplayStart('');
        // Default download window: catch up from the last day on file to today.
        setDownloadStart(stock.last_quote_date || subtractOneYear(today));
        setDownloadEnd(today);
        resetTableQuotes();
        loadDetailData(stock.symbol, DEFAULT_DISPLAY_RANGE, stock.last_quote_date || today);
        loadStats(stock.symbol);
    };

    const closeDetail = () => {
        detailSymbolRef.current = null;
        setDetailStock(null);
        setQuotePayload(null);
        setDividendPayload(null);
        setSplitPayload(null);
        setStats(null);
        setStatsError('');
        setDownloadNote('');
        setDisplayStart('');
        setDownloadStart('');
        setDownloadEnd('');
        resetTableQuotes();
        fetchStocks({ silent: true });
    };

    const detailSymbol = detailStock?.symbol || null;
    const detailMarketOpen = Boolean(stats?.market_open);

    /**
     * While the detail view is open and its market is trading, refresh the
     * statistics on the same cadence as the quotes service cache. Paused for a
     * hidden tab and torn down on unmount or on going back to the list.
     */
    useEffect(() => {
        if (!detailSymbol || !detailMarketOpen) return undefined;
        return pollWhileVisible(
            () => loadStats(detailSymbol, { background: true }),
            LIVE_POLL_MS,
        );
    }, [detailSymbol, detailMarketOpen, loadStats]);

    /** Live prices for the watchlist cards — only while the list is on screen. */
    const showingList = !detailStock;
    const watchlistSize = stocks.length;

    useEffect(() => {
        if (!showingList || watchlistSize === 0) return undefined;
        return pollWhileVisible(
            loadLiveQuotes,
            liveMarketOpen ? LIVE_POLL_MS : CLOSED_POLL_MS,
            { immediate: true },
        );
    }, [showingList, watchlistSize, liveMarketOpen, loadLiveQuotes]);

    const handleAdd = async (e) => {
        e.preventDefault();
        const sym = symbol.trim().toUpperCase();
        if (!sym) return;

        setError('');
        setAdding(true);
        try {
            const r = await fetch('/api/stocks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ symbol: sym }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Failed to add stock');
            setSymbol('');
            await fetchStocks();
        } catch (err) {
            setError(err.message);
        } finally {
            setAdding(false);
        }
    };

    const handleRemove = async (sym, e) => {
        if (e) e.stopPropagation();
        setError('');
        setRemoving(sym);
        try {
            const r = await fetch(`/api/stocks/${encodeURIComponent(sym)}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!r.ok) {
                const data = await r.json();
                throw new Error(data.error || 'Failed to remove stock');
            }
            setStocks(prev => prev.filter(s => s.symbol !== sym));
            if (detailStock && detailStock.symbol === sym) closeDetail();
        } catch (err) {
            setError(err.message);
        } finally {
            setRemoving(null);
        }
    };

    // Re-picking the current range is allowed: it doubles as a retry after a failed load.
    const handleDisplayRange = (rangeId) => {
        if (!detailStock) return;
        setDisplayRange(rangeId);
        loadDetailData(detailStock.symbol, rangeId, storedAnchor());
    };

    const applyDownloadPreset = useCallback(
        id => {
            if (!detailStock) return;
            const today = new Date();
            setDownloadEnd(formatLocalYMD(today));

            if (id === 'CATCHUP') {
                const last = quotePayload?.stored_range?.max || detailStock.last_quote_date;
                setDownloadStart(last || subtractOneYear(formatLocalYMD(today)));
                return;
            }

            const startD = new Date(today);
            switch (id) {
                case '1M':
                    startD.setMonth(startD.getMonth() - 1);
                    break;
                case '6M':
                    startD.setMonth(startD.getMonth() - 6);
                    break;
                case '1Y':
                    startD.setFullYear(startD.getFullYear() - 1);
                    break;
                case '5Y':
                    startD.setFullYear(startD.getFullYear() - 5);
                    break;
                case '10Y':
                    startD.setFullYear(startD.getFullYear() - 10);
                    break;
                case '20Y':
                    startD.setFullYear(startD.getFullYear() - 20);
                    break;
                default:
                    return;
            }
            setDownloadStart(formatLocalYMD(startD));
        },
        [detailStock, quotePayload?.stored_range?.max],
    );

    const handleDownload = async () => {
        if (!detailStock) return;
        if (!downloadStart || !downloadEnd) {
            setError('Choose a start and end date to download from Yahoo Finance.');
            return;
        }
        setError('');
        setDownloadNote('');
        setFetchingQuotes(true);
        try {
            const r = await fetch(
                `/api/stocks/${encodeURIComponent(detailStock.symbol)}/quotes/fetch`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ start: downloadStart, end: downloadEnd }),
                },
            );
            const data = await r.json();
            if (!r.ok) throw new Error(data.error || 'Download failed');
            const parts = [`${data.upserted ?? 0} price row${data.upserted === 1 ? '' : 's'}`];
            if (data.dividends_upserted) parts.push(`${data.dividends_upserted} dividend payments`);
            if (data.splits_upserted) parts.push(`${data.splits_upserted} splits`);
            setDownloadNote(
                `Saved ${parts.join(', ')} for ${downloadStart} – ${downloadEnd}.`,
            );
            // The download may have extended the history in either direction, so
            // reload the display range against the newest day now on file.
            const anchor =
                downloadEnd > storedAnchor() ? downloadEnd : storedAnchor();
            await loadDetailData(detailStock.symbol, displayRange, anchor);
            await fetchStocks({ silent: true });
        } catch (err) {
            setError(err.message);
        } finally {
            setFetchingQuotes(false);
        }
    };

    const formatListCloseValue = (price, currency) => {
        const code = currency && String(currency).trim().length === 3 ? String(currency).trim().toUpperCase() : null;
        if (code) {
            try {
                return new Intl.NumberFormat(undefined, {
                    style: 'currency',
                    currency: code,
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                }).format(price);
            } catch {
                /* fall through */
            }
        }
        return new Intl.NumberFormat(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price);
    };

    /** "+1.46 (+0.43%)" — the absolute move, then the percent. */
    const formatLiveChange = (live, currency) => {
        const abs = Number(live.change);
        const pct = Number(live.change_percent);
        const parts = [];
        if (Number.isFinite(abs)) {
            parts.push(`${abs >= 0 ? '+' : '−'}${formatListCloseValue(Math.abs(abs), currency)}`);
        }
        if (Number.isFinite(pct)) {
            parts.push(`(${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(2)}%)`);
        }
        return parts.join(' ') || null;
    };

    const formatBarDateLocal = (ymd) => {
        if (!ymd) return '';
        const [y, m, d] = String(ymd).split('-').map(Number);
        const dt = new Date(y, m - 1, d);
        return dt.toLocaleDateString(undefined, {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const formatDetail = (stock) => {
        const parts = [stock.sector, stock.industry, stock.country].filter(Boolean);
        return parts.join(' · ') || null;
    };

    const fmtVol = (v) => (v >= 1_000_000 ? `${(v / 1_000_000).toFixed(2)}M` : Math.round(v).toLocaleString());

    /** 4 → "4-for-1"; 0.5 → "1-for-2" (reverse split). */
    const formatSplitRatio = (ratio) => {
        const n = Number(ratio);
        if (!Number.isFinite(n) || n <= 0) return '—';
        if (n >= 1) {
            const whole = Number.isInteger(n) ? n : Number(n.toFixed(4));
            return `${whole}-for-1`;
        }
        const inverse = 1 / n;
        const whole = Number.isInteger(inverse) ? inverse : Number(inverse.toFixed(4));
        return `1-for-${whole}`;
    };

    const detailCurrency = stats?.currency || detailStock?.currency;
    const busy = loadingQuotes || fetchingQuotes;

    return (
        <div className="stocks">
            <div className="stocks-header">
                <h1>My Stocks</h1>
                {!detailStock && (
                    <form onSubmit={handleAdd} className="add-stock-form">
                        <input
                            className="add-stock-input"
                            type="text"
                            value={symbol}
                            onChange={e => setSymbol(e.target.value)}
                            placeholder="e.g. RY.TO"
                            disabled={adding}
                            maxLength={20}
                            aria-label="Stock symbol"
                        />
                        <button
                            type="submit"
                            className="add-stock-btn"
                            disabled={adding || !symbol.trim()}
                        >
                            {adding ? 'Adding…' : 'Add Stock'}
                        </button>
                    </form>
                )}
            </div>

            {error && <div className="alert-error">{error}</div>}

            {detailStock ? (
                <div className="stock-detail">
                    <button type="button" className="stock-detail-back" onClick={closeDetail}>
                        ← Back to list
                    </button>
                    <div className="stock-detail-header">
                        <div>
                            <h2 className="stock-detail-title">
                                <span className="stock-symbol">{detailStock.symbol}</span>
                                {detailStock.exchange && (
                                    <span className="stock-exchange">{detailStock.exchange}</span>
                                )}
                            </h2>
                            <div className="stock-name">{detailStock.name}</div>
                            {formatDetail(detailStock) && (
                                <div className="stock-detail-meta">{formatDetail(detailStock)}</div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="stock-remove-btn"
                            onClick={e => handleRemove(detailStock.symbol, e)}
                            disabled={removing === detailStock.symbol}
                        >
                            {removing === detailStock.symbol ? '…' : 'Remove from list'}
                        </button>
                    </div>

                    <StockStatsCard
                        symbol={detailStock.symbol}
                        stats={stats}
                        loading={loadingStats}
                        error={statsError}
                        onRetry={() => loadStats(detailStock.symbol)}
                        currency={detailStock.currency}
                    />

                    {/* ---- History on file: one range control for everything shown ---- */}
                    <section className="stock-history-card" aria-labelledby="stock-history-heading">
                        <div className="stock-history-head">
                            <h2 className="stock-section-heading" id="stock-history-heading">
                                History on file
                            </h2>
                            <p className="stock-section-lead">
                                Reads prices already saved in the database. Nothing here contacts
                                Yahoo — use <strong>Download history</strong> below to add data.
                            </p>
                        </div>

                        <span className="stock-chart-range-label" id="stock-display-range-label">
                            Show
                        </span>
                        <div
                            className="stock-chart-range-row"
                            role="group"
                            aria-labelledby="stock-display-range-label"
                        >
                            {DISPLAY_RANGES.map(p => (
                                <button
                                    key={p.id}
                                    type="button"
                                    className={`stock-chart-range-btn${displayRange === p.id ? ' is-active' : ''}`}
                                    onClick={() => handleDisplayRange(p.id)}
                                    disabled={busy}
                                    aria-pressed={displayRange === p.id}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <span className="stock-chart-range-label" id="stock-detail-view-label">
                            View
                        </span>
                        <div
                            className="stock-chart-range-row"
                            role="group"
                            aria-labelledby="stock-detail-view-label"
                        >
                            {[
                                { id: 'prices', label: 'Prices' },
                                { id: 'dividends', label: `Dividends${dividendRows.length ? ` (${dividendRows.length})` : ''}` },
                                { id: 'splits', label: `Splits${splitRows.length ? ` (${splitRows.length})` : ''}` },
                            ].map(v => (
                                <button
                                    key={v.id}
                                    type="button"
                                    className={`stock-chart-range-btn${detailViewMode === v.id ? ' is-active' : ''}`}
                                    onClick={() => setDetailViewMode(v.id)}
                                    disabled={busy}
                                    aria-pressed={detailViewMode === v.id}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>

                        {coverageShortfall && (
                            <p className="quote-toolbar-hint stock-coverage-note">
                                {DISPLAY_RANGE_LABELS[displayRange]} was requested, but the earliest
                                day on file for {detailStock.symbol} is{' '}
                                <strong>{coverageShortfall}</strong>. Download an earlier window
                                below to extend the chart.
                            </p>
                        )}

                        {loadingQuotes && <p className="stocks-empty">Loading saved history…</p>}

                        {!loadingQuotes && periodSummary && (
                            <dl className="stock-period-summary">
                                <div className="stock-period-item">
                                    <dt>Change</dt>
                                    <dd
                                        className={
                                            periodSummary.change >= 0
                                                ? 'stock-period-up'
                                                : 'stock-period-down'
                                        }
                                    >
                                        {periodSummary.change >= 0 ? '+' : '−'}
                                        {formatListCloseValue(Math.abs(periodSummary.change), detailCurrency)}
                                        {periodSummary.changePercent != null && (
                                            <span className="stock-period-pct">
                                                {' '}
                                                ({periodSummary.change >= 0 ? '+' : '−'}
                                                {Math.abs(periodSummary.changePercent).toFixed(2)}%)
                                            </span>
                                        )}
                                    </dd>
                                </div>
                                <div className="stock-period-item">
                                    <dt>First close</dt>
                                    <dd>{formatListCloseValue(periodSummary.firstClose, detailCurrency)}</dd>
                                </div>
                                <div className="stock-period-item">
                                    <dt>Last close</dt>
                                    <dd>{formatListCloseValue(periodSummary.lastClose, detailCurrency)}</dd>
                                </div>
                                <div className="stock-period-item">
                                    <dt>Period high</dt>
                                    <dd>{formatListCloseValue(periodSummary.high, detailCurrency)}</dd>
                                </div>
                                <div className="stock-period-item">
                                    <dt>Period low</dt>
                                    <dd>{formatListCloseValue(periodSummary.low, detailCurrency)}</dd>
                                </div>
                                <div className="stock-period-item">
                                    <dt>Average volume</dt>
                                    <dd>{fmtVol(periodSummary.avgVolume)}</dd>
                                </div>
                                <div className="stock-period-item">
                                    <dt>Trading days</dt>
                                    <dd>{periodSummary.sessions.toLocaleString()}</dd>
                                </div>
                                {periodSummary.dividendTotal > 0 && (
                                    <div className="stock-period-item">
                                        <dt>Dividends paid</dt>
                                        <dd>
                                            {formatListCloseValue(periodSummary.dividendTotal, detailCurrency)}
                                            <span className="stock-period-pct"> / share</span>
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        )}

                        {!loadingQuotes && detailViewMode === 'prices' && quotes.length > 0 && (
                            <div className="stock-chart-block">
                                <StockPriceChart quotes={quotes} currency={detailCurrency} />
                            </div>
                        )}

                        {!loadingQuotes && detailViewMode === 'dividends' && dividendRowsAsQuotes.length > 0 && (
                            <div className="stock-chart-block">
                                <StockPriceChart
                                    quotes={dividendRowsAsQuotes}
                                    currency={detailCurrency}
                                    variant="dividend"
                                />
                            </div>
                        )}

                        {!loadingQuotes && quotePayload?.stored_range && (
                            <p className="stored-range-line">
                                Prices on file: <strong>{quotePayload.stored_range.min}</strong>
                                {' — '}
                                <strong>{quotePayload.stored_range.max}</strong>
                                {quotePayload.total_rows != null && (
                                    <span className="stored-range-count">
                                        {' '}({quotePayload.total_rows} row
                                        {quotePayload.total_rows === 1 ? '' : 's'} in the span shown)
                                    </span>
                                )}
                            </p>
                        )}

                        {/* ---- Prices ---- */}
                        {!loadingQuotes && detailViewMode === 'prices' && quotePayload && (
                            <>
                                {quotePayload.truncated && (
                                    <div className="quotes-truncated-note">
                                        Showing the most recent {quotePayload.returned_rows} rows of {quotePayload.total_rows} in this span.
                                        Pick a shorter range to see every bar.
                                    </div>
                                )}
                                {quotes.length === 0 ? (
                                    <div className="stocks-empty stocks-empty-tight">
                                        <p>No prices saved for this span yet.</p>
                                        <p className="stocks-empty-hint">
                                            Use <strong>Download history</strong> below to load it from Yahoo.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <label className="quote-table-toggle">
                                            <input
                                                type="checkbox"
                                                checked={showPriceRows}
                                                onChange={e => setShowPriceRows(e.target.checked)}
                                            />
                                            Show individual prices
                                        </label>
                                        {showPriceRows ? (
                                            tableQuotes.length === 0 ? (
                                                <div className="stocks-empty stocks-empty-tight">
                                                    <p>No trading days in the latest year of this span.</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="quotes-table-summary">
                                                        Showing {tableQuotes.length} trading day{tableQuotes.length === 1 ? '' : 's'}
                                                        {tableOldestLoaded && (
                                                            <>
                                                                {' '}
                                                                from <strong>{tableOldestLoaded}</strong> through{' '}
                                                                <strong>{tableQuotes[tableQuotes.length - 1].date}</strong>
                                                            </>
                                                        )}
                                                    </p>
                                                    <div className="quotes-table-wrap">
                                                        <table className="quotes-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Date</th>
                                                                    <th>Open</th>
                                                                    <th>High</th>
                                                                    <th>Low</th>
                                                                    <th>Close</th>
                                                                    <th>Change</th>
                                                                    <th>Range</th>
                                                                    <th>Volume</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {tableRowsNewestFirst.map(row => {
                                                                    const dayChange = row.closing - row.opening;
                                                                    const dayPct = row.opening
                                                                        ? (dayChange / row.opening) * 100
                                                                        : null;
                                                                    return (
                                                                        <tr key={row.date}>
                                                                            <td className="quotes-td-date">{row.date}</td>
                                                                            <td>{row.opening?.toFixed?.(2) ?? row.opening}</td>
                                                                            <td>{row.high?.toFixed?.(2) ?? row.high}</td>
                                                                            <td>{row.low?.toFixed?.(2) ?? row.low}</td>
                                                                            <td>{row.closing?.toFixed?.(2) ?? row.closing}</td>
                                                                            <td
                                                                                className={
                                                                                    dayChange >= 0
                                                                                        ? 'quotes-td-change stock-period-up'
                                                                                        : 'quotes-td-change stock-period-down'
                                                                                }
                                                                            >
                                                                                {dayChange >= 0 ? '+' : '−'}
                                                                                {Math.abs(dayChange).toFixed(2)}
                                                                                {dayPct != null && (
                                                                                    <span className="quotes-td-change-pct">
                                                                                        {' '}
                                                                                        ({dayChange >= 0 ? '+' : '−'}
                                                                                        {Math.abs(dayPct).toFixed(2)}%)
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="quotes-td-vol">
                                                                                {(row.high - row.low).toFixed(2)}
                                                                            </td>
                                                                            <td className="quotes-td-vol">{fmtVol(row.volume)}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    {tableHasMore && (
                                                        <div className="quotes-table-footer">
                                                            <button
                                                                type="button"
                                                                className="quote-btn quote-btn-secondary"
                                                                onClick={loadMoreTable}
                                                                disabled={loadingMoreTable || busy}
                                                            >
                                                                {loadingMoreTable ? 'Loading…' : 'Load more (1 year)'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )
                                        ) : (
                                            <p className="quotes-table-summary">
                                                Individual prices are hidden to keep long histories fast.
                                            </p>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {/* ---- Dividends ---- */}
                        {!loadingQuotes && detailViewMode === 'dividends' && (
                            <>
                                {dividendPayload?.load_error ? (
                                    <p className="quote-toolbar-hint alert-error stocks-dividends-error">
                                        Dividends could not be loaded: {dividendPayload.load_error}
                                    </p>
                                ) : dividendRows.length === 0 ? (
                                    <div className="stocks-empty stocks-empty-tight">
                                        <p>No dividend payments on file for this span.</p>
                                        <p className="stocks-empty-hint">
                                            Many stocks pay no dividend; otherwise download a span that
                                            covers the ex-dividend dates.
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {dividendPayload?.stored_range && (
                                            <p className="stored-range-line">
                                                Dividends on file:{' '}
                                                <strong>{dividendPayload.stored_range.min}</strong>
                                                {' — '}
                                                <strong>{dividendPayload.stored_range.max}</strong>
                                                <span className="stored-range-count">
                                                    {' '}({dividendRows.length} payment
                                                    {dividendRows.length === 1 ? '' : 's'} in the span shown)
                                                </span>
                                            </p>
                                        )}
                                        <div className="quotes-table-wrap">
                                            <table className="quotes-table quotes-table--dividends">
                                                <thead>
                                                    <tr>
                                                        <th>Payment date</th>
                                                        <th>Amount per share</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {dividendRowsNewestFirst.map(row => (
                                                        <tr key={row.date}>
                                                            <td className="quotes-td-date">{row.date}</td>
                                                            <td>{formatListCloseValue(row.amount, detailCurrency)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* ---- Splits ---- */}
                        {!loadingQuotes && detailViewMode === 'splits' && (
                            <>
                                {splitPayload?.load_error ? (
                                    <p className="quote-toolbar-hint alert-error stocks-dividends-error">
                                        Splits could not be loaded: {splitPayload.load_error}
                                    </p>
                                ) : splitRows.length === 0 ? (
                                    <div className="stocks-empty stocks-empty-tight">
                                        <p>No share splits on file for this span.</p>
                                        <p className="stocks-empty-hint">
                                            Splits are rare — download a longer span to look further back.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="quotes-table-wrap">
                                        <table className="quotes-table quotes-table--dividends">
                                            <thead>
                                                <tr>
                                                    <th>Effective date</th>
                                                    <th>Split</th>
                                                    <th>Ratio</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {splitRowsNewestFirst.map(row => (
                                                    <tr key={row.date}>
                                                        <td className="quotes-td-date">{row.date}</td>
                                                        <td>{formatSplitRatio(row.ratio)}</td>
                                                        <td className="quotes-td-vol">{row.ratio}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </>
                        )}
                    </section>

                    {/* ---- Download: only touches what is stored, never the view above ---- */}
                    <section
                        className="stock-quote-range-card"
                        aria-labelledby="stock-download-heading"
                    >
                        <h2 className="stock-section-heading" id="stock-download-heading">
                            Download history for{' '}
                            <span className="stock-fetch-range-symbol">{detailStock.symbol}</span>
                        </h2>
                        <p className="stock-section-lead">
                            Pulls daily prices, dividends and splits from Yahoo Finance for this
                            window and saves them for <strong>{detailStock.symbol}</strong> only.
                            The view above then reloads at whatever range it is set to — these dates
                            do not change what is shown.
                        </p>

                        <div className="stock-fetch-presets">
                            <span className="stock-chart-range-label" id="stock-download-presets-label">
                                Quick window
                            </span>
                            <div
                                className="stock-chart-range-row"
                                role="group"
                                aria-labelledby="stock-download-presets-label"
                            >
                                {DOWNLOAD_PRESETS.map(p => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className="stock-chart-range-btn"
                                        onClick={() => applyDownloadPreset(p.id)}
                                        disabled={busy}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="quote-toolbar">
                            <label className="quote-date-label">
                                Start
                                <input
                                    type="date"
                                    value={downloadStart}
                                    onChange={e => setDownloadStart(e.target.value)}
                                    disabled={busy}
                                />
                            </label>
                            <label className="quote-date-label">
                                End
                                <input
                                    type="date"
                                    value={downloadEnd}
                                    onChange={e => setDownloadEnd(e.target.value)}
                                    disabled={busy}
                                />
                            </label>
                            <button
                                type="button"
                                className="quote-btn quote-btn-primary"
                                onClick={handleDownload}
                                disabled={busy || !downloadStart || !downloadEnd}
                            >
                                {fetchingQuotes ? 'Downloading…' : 'Download from Yahoo'}
                            </button>
                        </div>
                        {downloadNote && <p className="stock-download-note">{downloadNote}</p>}
                    </section>
                </div>
            ) : loadingList ? (
                <div className="stocks-empty"><p>Loading…</p></div>
            ) : stocks.length === 0 ? (
                <div className="stocks-empty">
                    <p>No stocks in your list yet.</p>
                    <p className="stocks-empty-hint">
                        Use the field above to add a stock by its Yahoo Finance symbol —
                        e.g. <strong>AAPL</strong>, <strong>RY.TO</strong>, <strong>SHOP.TO</strong>
                    </p>
                </div>
            ) : (
                <>
                    <div className="stocks-region-toolbar">
                        <span className="stock-chart-range-label" id="stocks-region-label">
                            Market
                        </span>
                        <div
                            className="stock-chart-range-row"
                            role="group"
                            aria-labelledby="stocks-region-label"
                        >
                            {REGION_FILTERS.map(f => (
                                <button
                                    key={f.id}
                                    type="button"
                                    className={`stock-chart-range-btn${regionFilter === f.id ? ' is-active' : ''}`}
                                    onClick={() => setRegionFilter(f.id)}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {displayedStocks.length === 0 ? (
                        <div className="stocks-empty stocks-empty-tight">
                            <p>No stocks match this market filter.</p>
                            <p className="stocks-empty-hint">
                                Try <strong>All</strong>, or check that Yahoo metadata (country / exchange) is present for
                                those symbols.
                            </p>
                        </div>
                    ) : (
                        <div className="stocks-grid">
                            {displayedStocks.map(stock => {
                        const live = liveQuotes[stock.symbol];
                        // The stored close stays the fallback: live prices only
                        // take over while that symbol's market is actually trading.
                        const tradingNow = Boolean(live && live.market_open && live.price != null);
                        const cardCurrency = stock.currency || live?.currency;
                        const livePrice = tradingNow
                            ? formatListCloseValue(live.price, cardCurrency)
                            : null;
                        const liveChange = tradingNow ? formatLiveChange(live, cardCurrency) : null;
                        const liveChangeClass = tradingNow
                            ? moveClass(live.change_percent ?? live.change)
                            : '';
                        const liveAt = tradingNow ? formatClockLocal(live.quote_time) : '';
                        const storedClose =
                            stock.last_close != null && stock.last_quote_date
                                ? formatListCloseValue(stock.last_close, cardCurrency)
                                : null;

                        let cardLabel = `View quotes for ${stock.symbol}`;
                        if (tradingNow) {
                            cardLabel = `View quotes for ${stock.symbol}, trading at ${livePrice}${liveChange ? `, ${liveChange}` : ''}`;
                        } else if (storedClose) {
                            cardLabel = `View quotes for ${stock.symbol}, last close ${storedClose} on ${stock.last_quote_date}`;
                        }

                        return (
                        <div
                            key={stock.symbol}
                            className="stock-card stock-card-clickable"
                            onClick={() => openDetail(stock)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openDetail(stock);
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={cardLabel}
                        >
                            <div className="stock-card-main">
                                <span className="stock-symbol">{stock.symbol}</span>
                                {stock.exchange && (
                                    <span className="stock-exchange">{stock.exchange}</span>
                                )}
                            </div>
                            <div className="stock-card-meta">
                                <div className="stock-name">{stock.name}</div>
                                {tradingNow ? (
                                    <div className="stock-card-latest">
                                        <div className="stock-card-latest-price">
                                            {livePrice}
                                            {liveChange && (
                                                <span
                                                    className={`stock-card-change${liveChangeClass ? ` ${liveChangeClass}` : ''}`}
                                                >
                                                    {liveChange}
                                                </span>
                                            )}
                                        </div>
                                        <div className="stock-card-latest-line">
                                            <span className="stock-card-live-badge">Live</span>
                                            {liveAt && <>{' '}{liveAt}</>}
                                            {storedClose && (
                                                <>
                                                    <span className="stock-card-latest-sep" aria-hidden="true">
                                                        {' · '}
                                                    </span>
                                                    <span className="stock-card-latest-label">Last close</span>
                                                    {' '}
                                                    {storedClose}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : storedClose ? (
                                    <div className="stock-card-latest">
                                        <div className="stock-card-latest-price">
                                            Last close {storedClose}
                                        </div>
                                        <div className="stock-card-latest-line">
                                            <span className="stock-card-latest-label">Trading day</span>
                                            {' '}
                                            {formatBarDateLocal(stock.last_quote_date)}
                                            {stock.quotes_fetched_at && (
                                                <>
                                                    <span className="stock-card-latest-sep" aria-hidden="true">
                                                        {' · '}
                                                    </span>
                                                    <span className="stock-card-latest-label">Updated</span>
                                                    {' '}
                                                    {new Date(stock.quotes_fetched_at).toLocaleString(undefined, {
                                                        dateStyle: 'medium',
                                                        timeStyle: 'short',
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="stock-card-latest stock-card-latest--empty">
                                        No stored prices yet — open to load quotes.
                                    </div>
                                )}
                                {formatDetail(stock) && (
                                    <div className="stock-detail">{formatDetail(stock)}</div>
                                )}
                            </div>
                            <div className="stock-card-right">
                                {stock.currency && (
                                    <span className="stock-currency">{stock.currency}</span>
                                )}
                                <button
                                    type="button"
                                    className="stock-remove-btn"
                                    onClick={e => handleRemove(stock.symbol, e)}
                                    disabled={removing === stock.symbol}
                                >
                                    {removing === stock.symbol ? '…' : 'Remove'}
                                </button>
                            </div>
                        </div>
                        );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Stocks;
